import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Navigate } from 'react-router-dom';
import { CERTIFICATIONS, INSTRUCTION, TEMPLATE_VERSION, certificationPreview, createUidSvg, downloadUidSvg, printUidSvg, qrArtwork, supportsTemplateVersion, websiteUrlForRecord } from './uidArtwork';

const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const API_PATH = '/quality/laser-engraving';
const FAMILIES = [
  { key: 'wave-smart', label: 'Wave Smart' },
  { key: 'wave-performance', label: 'Wave Performance' },
  { key: 'wave-ii', label: 'Wave II' },
  { key: 'wave-xs', label: 'Wave XS' },
  { key: 'wave-xl', label: 'Wave XL' },
];
const COMPONENTS = ['ELU', 'DIU', 'MON'];
const EMPTY_FORM = { sizeCode: '', customerCode: '', productCode: '', supplier: 'gpv', certifications: ['None', 'None', 'None', 'None'] };
const panelStyle = { padding: 20, marginTop: 18, border: '1px solid var(--ni-border-soft)', background: 'var(--ni-panel)', borderRadius: 10 };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16 };
const fieldStyle = { display: 'block', fontSize: 13, fontWeight: 700 };
const inputStyle = { width: '100%', marginTop: 8, boxSizing: 'border-box' };
const hintStyle = { display: 'block', marginTop: 6, color: 'var(--ni-text-muted)', fontSize: 12, fontWeight: 400, lineHeight: 1.5 };

function makeRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) throw new Error('This browser cannot create a label request. Use a current browser with a secure connection.');
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export default function LaserEngravingPage() {
  const auth = useAuth();
  const legacyTestId = new URLSearchParams(window.location.search).get('t');
  if (legacyTestId !== null) {
    return /^[0-9a-f]{24}$/.test(legacyTestId) ? <Navigate to={`/device/test/${legacyTestId}`} replace /> : <p role="alert">This test device link is invalid.</p>;
  }
  return <LaserEngravingSession key={`${auth.userId || 'anonymous'}:${auth.isSignedIn ? 'signed-in' : 'signed-out'}`} auth={auth} />;
}

function LaserEngravingSession({ auth }) {
  const { getToken, isLoaded, isSignedIn, userId } = auth;
  const [config, setConfig] = useState(null);
  const [testConfig, setTestConfig] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testLoadError, setTestLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [denied, setDenied] = useState(false);
  const [deepLinkId] = useState(() => new URLSearchParams(window.location.search).get('label') || '');
  const [family, setFamily] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    return query.has('label') || query.get('mode') === 'test' ? 'test' : 'wave-xl';
  });
  const [component, setComponent] = useState('MON');
  const [form, setForm] = useState(EMPTY_FORM);
  const [record, setRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [lastIssuedNumber, setLastIssuedNumber] = useState('');
  const [testStartingNumber, setTestStartingNumber] = useState('0');
  const [confirmReset, setConfirmReset] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [openingId, setOpeningId] = useState('');
  const [pendingRetry, setPendingRetry] = useState(false);
  const [recoveryBlocked, setRecoveryBlocked] = useState(false);
  const requestRef = useRef(null);
  const mountedRef = useRef(true);
  const submittingRef = useRef(false);
  const selectionRef = useRef(0);
  const historyRequestRef = useRef(0);
  const deepLinkOpened = useRef(false);
  const isTest = family === 'test';
  const pendingStorageKey = userId ? `ni-laser-engraving-pending:${isTest ? 'test:' : ''}${userId}` : '';
  const recordPath = isTest ? '/test/records' : '/records';
  const currentConfig = isTest ? testConfig : config;
  const active = (family === 'wave-xl' || (isTest && config?.capabilities?.sandbox === true)) && component === 'MON';

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const api = useCallback(async (suffix, options = {}) => {
    const token = await getToken();
    if (!token || !userId || !mountedRef.current) {
      const authError = new Error('Sign in again to use laser engraving.');
      authError.status = 401;
      throw authError;
    }
    const response = await window.fetch(`${API_BASE}${API_PATH}${suffix}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...options.headers },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const requestError = new Error(data.message || data.error || `Request failed (${response.status}).`);
      requestError.status = response.status;
      requestError.code = data.error;
      throw requestError;
    }
    return data;
  }, [getToken, userId]);

  useEffect(() => {
    setRecord(null);
    setRecords([]);
    requestRef.current = null;
    setPendingRetry(false);
    setRecoveryBlocked(false);
    setForm(EMPTY_FORM);
    setError('');
    setConfirmReset(false);
    setCopyStatus('');
    if (!isLoaded || !isSignedIn || !pendingStorageKey) return;
    try {
      const saved = window.sessionStorage.getItem(pendingStorageKey);
      if (!saved) return;
      const pending = JSON.parse(saved);
      const valid = pending.family === (isTest ? 'test' : 'wave-xl') && pending.component === 'MON' && pending.supplier === 'gpv'
        && /^\d{3}$/.test(pending.sizeCode) && Number(pending.sizeCode) > 0 && /^\d{3}$/.test(pending.customerCode)
        && /^\d{3}$/.test(pending.productCode) && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(pending.requestId)
        && Array.isArray(pending.certifications) && pending.certifications.length === 4 && pending.certifications.every(name => CERTIFICATIONS.includes(name))
        && (!isTest || typeof pending.testSessionId === 'string');
      if (!valid) throw new Error('Invalid pending label');
      requestRef.current = pending;
      setFamily(pending.family);
      setComponent(pending.component);
      setForm({ sizeCode: pending.sizeCode, customerCode: pending.customerCode, productCode: pending.productCode, supplier: pending.supplier, certifications: pending.certifications });
      setPendingRetry(true);
      setError('A label request was interrupted. Retry the same request to recover its saved number before creating another label.');
    } catch {
      setRecoveryBlocked(true);
      setError('The pending label request could not be recovered. Contact North Invent before creating another label in this tab.');
    }
  }, [isLoaded, isSignedIn, pendingStorageKey, isTest]);

  function clearStoredRequest(saved = false) {
    try { window.sessionStorage.removeItem(pendingStorageKey); } catch {
      setError(saved ? 'The label is saved, but the browser could not clear its recovery data. Reopening this tab may recover the same saved label again.' : 'The browser could not clear the label recovery data. Reopening this tab may retry the same request.');
    }
  }

  const loadHistory = useCallback(async () => {
    const selection = selectionRef.current;
    const request = ++historyRequestRef.current;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const data = await api(`${recordPath}?limit=25`);
      if (selection === selectionRef.current && request === historyRequestRef.current) setRecords(data.records || []);
    } catch (err) {
      if (selection === selectionRef.current && request === historyRequestRef.current) setHistoryError(err.message);
    } finally {
      if (selection === selectionRef.current && request === historyRequestRef.current) setHistoryLoading(false);
    }
  }, [api, recordPath]);

  const loadTestConfig = useCallback(async signal => {
    const selection = selectionRef.current;
    setTestLoading(true);
    setTestLoadError('');
    try {
      const data = await api('/test/config', { signal });
      if (!signal?.aborted && selection === selectionRef.current) setTestConfig(data);
      return data;
    } catch (err) {
      if (err.name !== 'AbortError' && !signal?.aborted && selection === selectionRef.current) setTestLoadError(err.message);
      return null;
    } finally {
      if (!signal?.aborted && selection === selectionRef.current) setTestLoading(false);
    }
  }, [api]);

  const load = useCallback(async signal => {
    setLoading(true);
    setLoadError('');
    setDenied(false);
    try {
      const data = await api('/config', { signal });
      if (signal?.aborted) return;
      setConfig(data);
      if (!data.permissions?.canGenerate) setDenied(true);
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (err.status === 401 || err.status === 403) setDenied(true);
      else setLoadError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isLoaded) return undefined;
    if (!isSignedIn) {
      setDenied(true);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [isLoaded, isSignedIn, load]);

  useEffect(() => {
    if (!isTest || config?.capabilities?.sandbox !== true) return undefined;
    const controller = new AbortController();
    loadTestConfig(controller.signal);
    return () => controller.abort();
  }, [isTest, config?.capabilities?.sandbox, loadTestConfig]);

  useEffect(() => {
    if (currentConfig?.permissions?.canGenerate && (!isTest || config?.capabilities?.sandbox === true)) loadHistory();
  }, [currentConfig?.permissions?.canGenerate, isTest, config?.capabilities?.sandbox, loadHistory]);

  useEffect(() => {
    if (!isTest || !testConfig || !requestRef.current?.testSessionId || requestRef.current.testSessionId === testConfig.counter?.sessionId) return;
    requestRef.current = null;
    setPendingRetry(false);
    setRecoveryBlocked(false);
    try { window.sessionStorage.removeItem(pendingStorageKey); } catch { /* A stale Test request is rejected by its session ID on the server. */ }
    setError('The test session was reset. Its interrupted request has been cleared; you can create a new test label.');
  }, [isTest, testConfig, pendingStorageKey]);

  const validCodes = /^\d{3}$/.test(form.sizeCode) && Number(form.sizeCode) > 0 && /^\d{1,3}$/.test(form.customerCode) && /^\d{1,3}$/.test(form.productCode);
  const draftProduct = validCodes ? `WE${form.sizeCode} ${form.customerCode.padStart(3, '0')} ${form.productCode.padStart(3, '0')}` : '';
  const artwork = useMemo(() => {
    if (!active) return { svg: '', error: '', websiteUrl: '' };
    try {
      if (record && !supportsTemplateVersion(record.templateVersion)) throw new Error('This saved label uses a different template version. Its original template is required for reprinting.');
      return { svg: createUidSvg(record || { productNumber: draftProduct, certifications: form.certifications, isSandbox: isTest }), error: '', websiteUrl: record ? websiteUrlForRecord(record) : '' };
    } catch (err) {
      return { svg: '', error: err.message, websiteUrl: '' };
    }
  }, [active, record, draftProduct, form.certifications, isTest]);

  function changeSelection(nextFamily, nextComponent) {
    if ((nextFamily === 'test') !== isTest) {
      selectionRef.current += 1;
      setRecords([]);
      setHistoryError('');
      setHistoryLoading(false);
      setTestConfig(null);
    }
    setFamily(nextFamily);
    setComponent(nextComponent);
    setRecord(null);
    setForm(EMPTY_FORM);
    setError('');
    setCopyStatus('');
    setConfirmReset(false);
    requestRef.current = null;
  }

  function changeField(key, value) {
    setForm(previous => ({ ...previous, [key]: value }));
    requestRef.current = null;
    setError('');
    setCopyStatus('');
  }

  function changeCertification(index, value) {
    changeField('certifications', form.certifications.map((name, slot) => slot === index ? value : name));
  }

  async function generate() {
    if (submittingRef.current || record || openingId || initializing || recoveryBlocked || !active || !validCodes || !currentConfig?.counter?.initialized || !currentConfig?.permissions?.canGenerate || (!pendingRetry && config?.capabilities?.deviceQrLinks !== true)) return;
    submittingRef.current = true;
    setSaving(true);
    setError('');
    let requestSent = false;
    try {
      if (!requestRef.current) {
        requestRef.current = {
          family, component, supplier: form.supplier, sizeCode: form.sizeCode,
          customerCode: form.customerCode.padStart(3, '0'), productCode: form.productCode.padStart(3, '0'),
          certifications: [...form.certifications], requestId: makeRequestId(),
          ...(isTest ? { testSessionId: currentConfig.counter.sessionId } : {}),
        };
      }
      if (!pendingStorageKey) throw new Error('Sign in again before creating a label.');
      try {
        window.sessionStorage.setItem(pendingStorageKey, JSON.stringify(requestRef.current));
      } catch {
        throw new Error(pendingRetry ? 'The browser could not update label recovery data. The existing pending request is retained; enable session storage to recover it.' : 'The browser could not save label recovery data. Enable session storage before generating a label. No number was allocated.');
      }
      requestSent = true;
      const data = await api(recordPath, { method: 'POST', body: JSON.stringify(requestRef.current) });
      if (!data.record?.serviceTagNumber || !data.record?.productNumber) throw new Error('The server did not return the saved label. Retry to recover this request.');
      if (Boolean(data.record.isSandbox) !== isTest || data.record.family !== (isTest ? 'test' : 'wave-xl') || (isTest && data.record.testSessionId !== currentConfig.counter.sessionId)) throw new Error('The server returned a label from a different environment. Retry to recover this request.');
      setRecord(data.record);
      setPendingRetry(false);
      clearStoredRequest(true);
      const updateConfig = isTest ? setTestConfig : setConfig;
      updateConfig(previous => ({ ...previous, counter: { ...previous.counter, current: Math.max(Number(previous.counter.current) || 0, Number(data.record.runningNumber)) } }));
      loadHistory();
    } catch (err) {
      if (isTest && err.code === 'test_session_changed') {
        requestRef.current = null;
        setPendingRetry(false);
        setRecoveryBlocked(false);
        clearStoredRequest();
        setRecord(null);
        setError('The test session was reset. Its interrupted request has been cleared; you can create a new test label.');
        await Promise.all([loadTestConfig(), loadHistory()]);
        return;
      }
      const uncertain = pendingRetry || (requestSent && (!err.status || err.status >= 500));
      setPendingRetry(uncertain);
      const message = err.status === 401 ? 'Your session has expired. Sign in again, then retry this label request.' : err.message;
      setError(uncertain ? `${message} Retry this same request to retrieve its saved number; a retry does not allocate a second number.` : message);
      if (!uncertain) {
        requestRef.current = null;
        clearStoredRequest();
      }
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  async function initializeCounter() {
    if (initializing || !config?.permissions?.canInitialize || !/^\d{1,6}$/.test(lastIssuedNumber)) return;
    setInitializing(true);
    setError('');
    try {
      await api('/counter', { method: 'POST', body: JSON.stringify({ lastIssuedNumber: lastIssuedNumber.padStart(6, '0') }) });
      setLastIssuedNumber('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setInitializing(false);
    }
  }

  async function resetTestCounter() {
    if (!isTest || initializing || saving || openingId || !testConfig?.permissions?.canReset || !/^\d{1,6}$/.test(testStartingNumber) || (testConfig.counter?.initialized && !confirmReset)) return;
    setInitializing(true);
    setError('');
    setRecord(null);
    try {
      const data = await api('/test/reset', { method: 'POST', body: JSON.stringify({ lastIssuedNumber: testStartingNumber.padStart(6, '0'), sessionId: testConfig.counter?.sessionId || null }) });
      setTestConfig(data);
      setRecord(null);
      setRecords([]);
      requestRef.current = null;
      setPendingRetry(false);
      setRecoveryBlocked(false);
      clearStoredRequest();
      setConfirmReset(false);
      setCopyStatus('');
      await loadHistory();
    } catch (err) {
      // A reset may have completed even if its response was lost. Reload before showing any saved Test artwork.
      setRecord(null);
      setError(err.message);
      await Promise.all([loadTestConfig(), loadHistory()]);
    } finally {
      setInitializing(false);
    }
  }

  const openRecord = useCallback(async id => {
    if (openingId || saving || initializing || pendingRetry || recoveryBlocked) return;
    setOpeningId(id);
    setError('');
    setCopyStatus('');
    try {
      const data = await api(`${recordPath}/${encodeURIComponent(id)}`);
      if (!data.record) throw new Error('The saved label could not be loaded.');
      if (Boolean(data.record.isSandbox) !== isTest || data.record.family !== (isTest ? 'test' : 'wave-xl') || (isTest && data.record.testSessionId !== testConfig?.counter?.sessionId)) throw new Error('This label does not belong to the current test session or environment.');
      setRecord(data.record);
      setFamily(data.record.family);
      setComponent(data.record.component);
      setForm({ sizeCode: data.record.sizeCode, customerCode: data.record.customerCode, productCode: data.record.productCode, supplier: data.record.supplier, certifications: data.record.certifications });
      requestRef.current = null;
    } catch (err) {
      setError(err.message);
    } finally {
      setOpeningId('');
    }
  }, [api, recordPath, isTest, testConfig?.counter?.sessionId, openingId, saving, initializing, pendingRetry, recoveryBlocked]);

  useEffect(() => {
    if (!deepLinkId || deepLinkOpened.current || !isTest || !testConfig || testLoading || pendingRetry || recoveryBlocked) return;
    deepLinkOpened.current = true;
    if (!/^[0-9a-f]{24}$/i.test(deepLinkId)) {
      setError('This test label link is invalid.');
      return;
    }
    openRecord(deepLinkId);
  }, [deepLinkId, isTest, testConfig, testLoading, pendingRetry, recoveryBlocked, openRecord]);

  function newLabel() {
    setRecord(null);
    requestRef.current = null;
    setError('');
    setCopyStatus('');
  }

  async function copyServiceTag() {
    try {
      await navigator.clipboard.writeText(record.serviceTagNumber);
      setCopyStatus('Service tag copied.');
    } catch {
      setCopyStatus('Select the service tag number below and copy it.');
    }
  }

  function printLabel() {
    try { printUidSvg(artwork.svg, record.serviceTagNumber); } catch (err) { setError(err.message); }
  }

  const families = (config?.families?.length ? config.families : FAMILIES).filter(item => item.key !== 'test' || config?.capabilities?.sandbox === true);
  const supplierOptions = config?.suppliers?.length ? config.suppliers : [{ key: 'gpv', label: 'GPV', prefix: 'C' }];
  const locked = Boolean(record) || saving || initializing || pendingRetry || recoveryBlocked || Boolean(openingId);
  const navigationLocked = saving || initializing || pendingRetry || recoveryBlocked || Boolean(openingId);

  return (
    <div>
      <h2 style={{ fontSize: 22, margin: '0 0 8px' }}>Laser Engraving</h2>
      <p style={{ color: 'var(--ni-text-muted)', fontSize: 14, margin: '0 0 20px' }}>Create a UID label and register its device before production.</p>
      {loading || !isLoaded ? <div role="status" style={panelStyle}>Loading laser engraving…</div> : denied ? <div role="alert" style={panelStyle}>Your account does not have access to laser engraving.</div> : loadError ? (
        <div role="alert" style={panelStyle}><p>{loadError}</p><button className="ni-button" onClick={() => load()}>Retry loading</button></div>
      ) : config && (
        <>
          <section aria-label="Product selection" style={panelStyle}>
            <label style={fieldStyle}>Product family
              <select className="ni-select" style={{ ...inputStyle, maxWidth: 360 }} value={family} onChange={event => changeSelection(event.target.value, 'MON')} disabled={navigationLocked}>
                {families.map(item => <option value={item.key} key={item.key}>{item.label}</option>)}
              </select>
            </label>
            <div role="group" aria-label="Component" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              {COMPONENTS.map(item => <button key={item} className="ni-button" aria-pressed={component === item} disabled={navigationLocked} style={{ background: component === item ? 'var(--ni-green)' : undefined, color: component === item ? 'var(--ni-ink)' : undefined }} onClick={() => changeSelection(family, item)}>{item}</button>)}
            </div>
          </section>
          {isTest && config.capabilities?.sandbox !== true ? <section role="status" style={panelStyle}>Test mode is not available on this backend yet.</section> : !active ? <section style={panelStyle}><h3 style={{ marginTop: 0 }}>{families.find(item => item.key === family)?.label} · {component}</h3><p className="ni-muted">No engraving template yet. Wave XL MON is the first available template.</p></section> : isTest && (testLoading || !testConfig || testLoadError) ? <section style={panelStyle}>{testLoading ? <p role="status">Loading your test session…</p> : <><p role="alert">{testLoadError || 'Your test session could not be loaded.'}</p><button className="ni-button" onClick={() => loadTestConfig()}>Retry test loading</button></>}</section> : (
            <>
              {isTest ? <section aria-label="Test mode" style={panelStyle}><strong>Test · Wave XL MON copy</strong><p style={{ marginBottom: 0 }}>Your own disposable test labels and counter. Test service tags begin with TEST-. No production devices are created and the shared GPV running number is unaffected.</p></section> : config.isTestUser && <p role="status" style={{ color: 'var(--ni-text-muted)' }}>This account’s production labels use the shared GPV sequence. Select Test for disposable labels with a separate counter.</p>}
              {isTest && <section aria-labelledby="test-counter-heading" style={panelStyle}>
                <h3 id="test-counter-heading" style={{ marginTop: 0 }}>Test running number</h3>
                <p className="ni-muted">Choose any last-issued test number. The next test label uses the following number. You can reset this whenever you want.</p>
                {testConfig.counter?.initialized && <p>Current test number: <strong>{String(testConfig.counter.current).padStart(6, '0')}</strong></p>}
                <div style={{ maxWidth: 420 }}>
                  <label style={fieldStyle}>Last-issued test number<input className="ni-input" style={inputStyle} inputMode="numeric" maxLength={6} value={testStartingNumber} onChange={event => { setTestStartingNumber(event.target.value); setConfirmReset(false); }} disabled={initializing || saving || Boolean(openingId)} /></label>
                  {testConfig.counter?.initialized ? confirmReset ? <div style={{ marginTop: 14 }}><p>Delete all of your test labels and restart your test counter at {testStartingNumber.padStart(6, '0')}? Production data is unaffected.</p><button className="ni-button" disabled={initializing || saving || Boolean(openingId) || !/^\d{1,6}$/.test(testStartingNumber) || !testConfig.permissions?.canReset} onClick={resetTestCounter}>{initializing ? 'Resetting…' : 'Confirm reset of my test data'}</button><button className="ni-button" style={{ marginLeft: 10 }} disabled={initializing} onClick={() => setConfirmReset(false)}>Cancel</button></div> : <button className="ni-button" style={{ marginTop: 14 }} disabled={saving || Boolean(openingId) || !/^\d{1,6}$/.test(testStartingNumber) || !testConfig.permissions?.canReset} onClick={() => setConfirmReset(true)}>Reset my test data and counter</button> : <button className="ni-button" style={{ marginTop: 14 }} disabled={initializing || saving || !/^\d{1,6}$/.test(testStartingNumber) || !testConfig.permissions?.canReset} onClick={resetTestCounter}>{initializing ? 'Setting up…' : 'Set test starting number'}</button>}
                </div>
              </section>}
              {!isTest && !config.counter?.initialized && (
                <section aria-labelledby="counter-heading" style={panelStyle}>
                  <h3 id="counter-heading" style={{ marginTop: 0 }}>GPV running number setup required</h3>
                  <p className="ni-muted">North Invent must confirm the last number already issued in GPV’s common sequence before the app can generate labels.</p>
                  {config.permissions?.canInitialize ? <div style={{ maxWidth: 420 }}>
                    <label style={fieldStyle}>Confirmed last-issued GPV number
                      <input className="ni-input" style={inputStyle} inputMode="numeric" maxLength={6} placeholder="000123" value={lastIssuedNumber} onChange={event => setLastIssuedNumber(event.target.value)} disabled={initializing} />
                    </label>
                    <p style={hintStyle}>This sets the starting point once. The next generated label uses the following number. Enter 0 only if no numbers have ever been issued.</p>
                    <button className="ni-button" style={{ marginTop: 14 }} disabled={initializing || !/^\d{1,6}$/.test(lastIssuedNumber)} onClick={initializeCounter}>{initializing ? 'Initializing…' : 'Initialize shared GPV counter'}</button>
                  </div> : <p>Ask North Invent to initialize the counter.</p>}
                </section>
              )}
              <section aria-labelledby="variables-heading" style={panelStyle}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
                  <div>
                    <h3 id="variables-heading" style={{ margin: 0 }}>{isTest ? 'Test · ' : ''}{INSTRUCTION.name}</h3>
                    <p style={{ ...hintStyle, marginBottom: 0 }}>Instruction {INSTRUCTION.documentNumber} · Revision {INSTRUCTION.revision}</p>
                  </div>
                  {record && <button className="ni-button" disabled={Boolean(openingId)} onClick={newLabel}>New label</button>}
                </div>
                <div style={gridStyle}>
                  <label style={fieldStyle}>Size code (3 digits)
                    <input className="ni-input" style={inputStyle} inputMode="numeric" maxLength={3} placeholder="650" value={form.sizeCode} onChange={event => changeField('sizeCode', event.target.value)} disabled={locked} />
                    <span style={hintStyle}>650 = 65″ · 430 = 43″</span>
                  </label>
                  <label style={fieldStyle}>Customer-specific code
                    <input className="ni-input" style={inputStyle} inputMode="numeric" maxLength={3} placeholder="000" value={form.customerCode} onChange={event => changeField('customerCode', event.target.value)} disabled={locked} />
                    <span style={hintStyle}>Middle three product P/N digits.</span>
                  </label>
                  <label style={fieldStyle}>Product-specific code
                    <input className="ni-input" style={inputStyle} inputMode="numeric" maxLength={3} placeholder="676" value={form.productCode} onChange={event => changeField('productCode', event.target.value)} disabled={locked} />
                    <span style={hintStyle}>Last three product P/N digits.</span>
                  </label>
                  <label style={fieldStyle}>Production supplier
                    <select className="ni-select" style={inputStyle} value={form.supplier} onChange={event => changeField('supplier', event.target.value)} disabled={locked}>
                      {supplierOptions.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}
                    </select>
                    <span style={hintStyle}>GPV gives service tag prefix C.</span>
                  </label>
                </div>
                <h4 style={{ marginBottom: 14, marginTop: 26 }}>Certifications · positions #1–4 at the right end</h4>
                <div style={gridStyle}>
                  {form.certifications.map((name, index) => <label key={index} style={fieldStyle}>Position #{index + 1}
                    <select aria-label={`Position #${index + 1}`} className="ni-select" style={inputStyle} value={name} disabled={locked} onChange={event => changeCertification(index, event.target.value)}>
                      {CERTIFICATIONS.map(cert => <option key={cert} value={cert}>{cert}</option>)}
                    </select>
                    <div style={{ display: 'flex', alignItems: 'center', width: 58, height: 48, background: name === 'None' ? undefined : 'white', borderRadius: 4, marginTop: 10 }}>
                      {name === 'None' ? <span style={hintStyle}>Empty</span> : <div style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: certificationPreview(name) }} />}
                    </div>
                  </label>)}
                </div>
                {!record && <div style={{ marginTop: 24 }}>
                  <p className="ni-muted" style={{ fontSize: 13 }}>{isTest ? 'Generating saves a disposable label and uses your next test number. It does not create a production device. Previewing does not use a number.' : 'The app assigns the next number in one shared GPV sequence and saves the device when you generate. Previewing does not use a number.'}</p>
                  {!pendingRetry && config.capabilities?.deviceQrLinks !== true && <p role="status" style={hintStyle}>Device QR links are not available yet. New label generation is temporarily unavailable.</p>}
                  <button className="ni-button" style={{ background: 'var(--ni-green)', color: 'var(--ni-ink)', fontWeight: 700 }} disabled={!validCodes || !currentConfig?.counter?.initialized || saving || initializing || recoveryBlocked || Boolean(openingId) || !currentConfig?.permissions?.canGenerate || (!pendingRetry && config.capabilities?.deviceQrLinks !== true)} onClick={generate}>{saving ? 'Generating and saving…' : pendingRetry ? 'Retry same label request' : isTest ? 'Generate test label' : 'Generate label and create device'}</button>
                </div>}
              </section>
              {error && <p role="alert" style={{ color: 'var(--ni-red)', overflowWrap: 'anywhere' }}>{error}</p>}
              <section aria-labelledby="preview-heading" style={panelStyle}>
                <h3 id="preview-heading" style={{ marginTop: 0 }}>UID label · 366 × 17 mm · 1:1</h3>
                {artwork.error ? <p role="alert">{artwork.error}</p> : <div aria-label="UID layout preview" style={{ overflowX: 'auto', background: 'white', padding: '12px 0' }}><div style={{ width: '366mm', height: '17mm', minWidth: '366mm' }} dangerouslySetInnerHTML={{ __html: artwork.svg }} /></div>}
                <p style={{ ...hintStyle, marginTop: 12 }}>Scroll to view the full strip. Screen scale depends on browser zoom and display settings. Print or save PDF on A3 landscape at 100% with headers and footers off.</p>
                {record ? (
                  <>
                    <dl style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '10px 18px', marginTop: 22 }}>
                      <dt>Product P/N</dt><dd style={{ margin: 0, fontFamily: 'monospace', userSelect: 'all' }}>{record.productNumber}</dd>
                      <dt>SERVICE:</dt><dd style={{ margin: 0, fontFamily: 'monospace', userSelect: 'all' }}>{record.serviceTagNumber}</dd>
                      <dt>{isTest ? 'Test running number' : 'GPV running number'}</dt><dd style={{ margin: 0, fontFamily: 'monospace' }}>{record.runningNumber}</dd>
                    </dl>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      <button className="ni-button" onClick={copyServiceTag}>Copy service tag</button>
                      <button className="ni-button" disabled={Boolean(artwork.error)} onClick={() => downloadUidSvg(artwork.svg, record.serviceTagNumber)}>Download SVG</button>
                      <button className="ni-button" disabled={Boolean(artwork.error)} onClick={printLabel}>Print / PDF 1:1</button>
                      {record.deviceId && <a className="ni-button" href={isTest ? `/device/test/${encodeURIComponent(record.deviceId)}` : `/device/${encodeURIComponent(record.deviceId)}`}>{isTest ? 'Open test device' : 'Open device'}</a>}
                    </div>
                    {copyStatus && <p role="status" style={hintStyle}>{copyStatus}</p>}
                    {!artwork.error && artwork.websiteUrl && <details aria-label="Enlarged QR codes" style={{ marginTop: 20 }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Enlarge QR codes for scanning</summary>
                      <p style={hintStyle}>Enlarged screen view for scanning. The printed UID label remains 366 × 17 mm.</p>
                      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 16 }}>
                        {[
                          { title: 'Service tag', payload: record.serviceTagNumber },
                          { title: 'Website', payload: artwork.websiteUrl },
                        ].map(item => <figure key={item.title} style={{ margin: 0, maxWidth: '100%' }}>
                          <figcaption style={{ fontWeight: 700, marginBottom: 10 }}>{item.title}</figcaption>
                          <svg xmlns="http://www.w3.org/2000/svg" role="img" aria-label={`${item.title} QR code`} viewBox="0 0 15 15" width="240" height="240" style={{ display: 'block', maxWidth: '100%', background: 'white' }} dangerouslySetInnerHTML={{ __html: qrArtwork(item.payload, { x: 0, y: 0, size: 15 }) }} />
                        </figure>)}
                      </div>
                    </details>}
                    <p style={{ ...hintStyle, marginTop: 16, overflowWrap: 'anywhere' }}>Saved label · {record.templateVersion || TEMPLATE_VERSION} · {new Date(record.createdAt).toLocaleString()}<br />Service QR: {record.serviceTagNumber} · Website QR: {artwork.websiteUrl || 'Unavailable: saved link requires review.'}<br />{record.deviceUrl === undefined && 'Legacy label: its website QR retains the service homepage.'}<br />Download and reprint use this saved identity and do not allocate a number.</p>
                  </>
                ) : <p style={{ ...hintStyle, marginTop: 18 }}>Product P/N: {draftProduct || 'Complete the three codes.'}<br />SERVICE: Assigned when the label is generated and saved.<br />Website QR: {isTest ? 'Opens the saved test device. No production device is created.' : 'The saved device link is assigned when the device is created.'}</p>}
              </section>
            </>
          )}
          <section aria-labelledby="history-heading" style={panelStyle}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <h3 id="history-heading" style={{ margin: 0 }}>{isTest ? 'Your test labels' : 'Recent labels'}</h3>
              <button className="ni-button" onClick={loadHistory} disabled={historyLoading || initializing || (isTest && (!testConfig || config.capabilities?.sandbox !== true))}>Refresh</button>
            </div>
            {historyLoading ? <p role="status">Loading saved labels…</p> : historyError ? <p role="alert">{historyError}</p> : records.length === 0 ? <p className="ni-muted">No labels generated yet.</p> : <div className="ni-table-wrap" style={{ marginTop: 16 }}>
              <table className="ni-table" style={{ width: '100%' }}><thead><tr><th>Service tag</th><th>Product P/N</th><th>Created</th><th>Label</th></tr></thead><tbody>
                {records.map(item => <tr key={item.id || item._id}><td style={{ fontFamily: 'monospace' }}>{item.serviceTagNumber}</td><td style={{ fontFamily: 'monospace' }}>{item.productNumber}</td><td>{new Date(item.createdAt).toLocaleString()}</td><td><button className="ni-button" disabled={navigationLocked} onClick={() => openRecord(item.id || item._id)}>{openingId === (item.id || item._id) ? 'Opening…' : `Reopen ${item.serviceTagNumber}`}</button></td></tr>)}
              </tbody></table>
            </div>}
          </section>
        </>
      )}
    </div>
  );
}
